import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';
import { Logo } from '../components/Logo';

const EFFECTIVE_DATE = 'February 21, 2026';
const COMPANY = 'CyBoard';
const SUPPORT_EMAIL = 'support@cyboard.games';
const WEBSITE = 'https://cyboard.games';

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#06060A] text-white" data-testid="privacy-policy-page">
      <header className="sticky top-0 z-10 backdrop-blur-md bg-[#06060A]/80 border-b border-white/[0.06]">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-full bg-[#181824] flex items-center justify-center" data-testid="back-button">
            <ArrowLeft size={18} weight="bold" />
          </button>
          <div className="flex-1">
            <Logo variant="full" size="sm" clickable={false} showTagline={false} />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8 pb-20">
        <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>Privacy Policy</h1>
        <p className="text-[#71717A] text-sm mb-8">Effective {EFFECTIVE_DATE}</p>

        <article className="prose-cyboard space-y-6 text-[#E4E4E7] text-[15px] leading-relaxed">

          <section>
            <p>
              {COMPANY} ("we", "us", "our") provides a mobile and web application that helps players of
              tabletop board games and card games discover each other, organize game sessions, track match
              results, and connect with local game shops. This Privacy Policy explains what we collect, how
              we use it, who we share it with, and the choices you have. It applies to the {COMPANY} mobile
              app, the {COMPANY} website at {WEBSITE}, and all related services (together, the "Service").
            </p>
            <p>
              By creating an account or using the Service you agree to this Privacy Policy. If you do not
              agree, please do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">1. Information We Collect</h2>

            <h3 className="text-base font-semibold text-white mt-4 mb-2">a) Account information you provide</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>Email address, display name, username, and password (hashed).</li>
              <li>Optional profile data: bio, avatar image, preferred games, play style, and a self-reported city/region.</li>
              <li>If you sign in with Google or Apple, we receive your name, email address, and (Google only) profile picture from that provider. We do not receive your social-network contacts or post on your behalf.</li>
            </ul>

            <h3 className="text-base font-semibold text-white mt-4 mb-2">b) Content you create</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>Game sessions you host (title, description, date/time, location).</li>
              <li>Messages you send in session chats and direct messages.</li>
              <li>Match records, reviews, social-feed posts, and other in-app contributions.</li>
              <li>Photos you optionally capture or upload for board-state detection (analyzed in real time; not retained beyond the analysis unless you save them to a session).</li>
            </ul>

            <h3 className="text-base font-semibold text-white mt-4 mb-2">c) Location data</h3>
            <p>
              We collect the city and ZIP/postal code you choose to share for matchmaking. <strong>We do not
              collect precise GPS coordinates and we do not track your location in the background.</strong> The
              exact street address of any private event you host is shared only with players you explicitly
              approve.
            </p>

            <h3 className="text-base font-semibold text-white mt-4 mb-2">d) Device and usage information</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>Device type, operating system, app version, language, and time zone.</li>
              <li>Crash logs and basic performance metrics.</li>
              <li>Pages visited and features used, to improve the product.</li>
            </ul>

            <h3 className="text-base font-semibold text-white mt-4 mb-2">e) Push notification tokens</h3>
            <p>
              If you opt in to push notifications, we store the device token issued by Apple Push Notification
              Service (APNs), Google Firebase Cloud Messaging (FCM), or the Web Push standard, used only to
              deliver notifications you have opted in to receive.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">2. How We Use Your Information</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>To create and operate your account.</li>
              <li>To match you with nearby players and recommend events.</li>
              <li>To deliver in-app notifications, emails (e.g., password resets), and push notifications you opt in to.</li>
              <li>To compute leaderboards, rankings, achievements, and aggregated game statistics.</li>
              <li>To analyze a board image you capture (computer vision) for game-state assistance — these images are processed transiently and not used to train third-party models.</li>
              <li>To detect, investigate, and prevent abuse, fraud, harassment, or violations of our Terms.</li>
              <li>To comply with legal obligations and respond to lawful requests.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">3. How We Share Information</h2>
            <p>We do <strong>not</strong> sell your personal data. We share data only as described below:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Other players:</strong> Your public profile (name, username, avatar, city, level, badges, public match history) is visible to other signed-in users. Private session locations are only revealed to approved guests.</li>
              <li><strong>Service providers:</strong> We use third-party processors to operate the Service: MongoDB Atlas (database hosting), Microsoft Graph (transactional email), Apple/Google/web-push providers (notifications), and OpenAI (computer-vision board detection, transient processing only). These vendors are contractually limited to processing data on our behalf.</li>
              <li><strong>Authentication providers:</strong> Google and Apple receive limited data only when you choose to sign in with them.</li>
              <li><strong>Game shops & event hosts:</strong> If you join an event or check in at a shop, that host sees the same public profile information any player can see.</li>
              <li><strong>Legal:</strong> We may disclose information when required by law, valid legal process, to enforce our Terms, or to protect the safety of users.</li>
              <li><strong>Business transfers:</strong> If {COMPANY} is acquired or merged, your data may be transferred to the successor entity, subject to this Privacy Policy.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">3a. AI Features &amp; Third-Party AI Processing</h2>
            <p>
              CyBoard offers optional AI-powered features that send some of your input to a third-party AI provider for
              processing. We want you to know exactly what is sent and to whom, and we ask for your consent the first time
              you use any of these features.
            </p>
            <h3 className="text-base font-semibold text-white mt-4 mb-2">a) Which features use AI</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>CyBoard AI Assistant</strong> (rule questions, strategy tips, game help).</li>
              <li><strong>"Rules from the Web"</strong> AI summarization of game rules.</li>
              <li><strong>Board Scan / CV Board Detection</strong> (photograph a board, AI analyzes the position).</li>
              <li><strong>Voice Q&amp;A</strong> (if enabled), which transcribes your spoken question and answers it.</li>
            </ul>
            <h3 className="text-base font-semibold text-white mt-4 mb-2">b) Which third party performs the AI processing</h3>
            <p>
              <strong>OpenAI, L.L.C.</strong> (san.francisco, california, usa) — specifically the <strong>GPT-4o</strong> model
              family — is the AI provider behind all of the features listed above. CyBoard accesses OpenAI through the
              Emergent LLM API on a server-to-server basis. OpenAI is a sub-processor acting on our behalf and is
              contractually bound not to use your inputs to train their general models (OpenAI&apos;s API data-usage
              policy, in effect as of the Effective Date of this Policy).
            </p>
            <h3 className="text-base font-semibold text-white mt-4 mb-2">c) What is sent to OpenAI</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>The text of your question or prompt.</li>
              <li>The name of the game you are asking about (e.g., &quot;Catan&quot;).</li>
              <li>For Board Scan: the photograph you choose to upload.</li>
              <li>For Voice Q&amp;A: an audio recording of the question you record.</li>
              <li>A small amount of session context so follow-up questions make sense.</li>
            </ul>
            <h3 className="text-base font-semibold text-white mt-4 mb-2">d) What is NOT sent</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>Your email address, password, account ID, or login token.</li>
              <li>Your friends list, direct-message contents, or social-feed posts.</li>
              <li>Your precise location, device identifiers, or push tokens.</li>
              <li>Payment information of any kind.</li>
            </ul>
            <h3 className="text-base font-semibold text-white mt-4 mb-2">e) Retention</h3>
            <p>
              OpenAI processes your inputs transiently to generate a response. Per OpenAI&apos;s API terms, your inputs
              are retained by OpenAI for a maximum of 30 days for abuse-monitoring only and are not used to train their
              models. CyBoard caches AI responses (not the raw photo) in your own account so we don&apos;t have to re-ask
              the same question; you can clear this cache from Profile → Settings.
            </p>
            <h3 className="text-base font-semibold text-white mt-4 mb-2">f) Your consent &amp; control</h3>
            <p>
              The first time you tap any AI feature, CyBoard shows a one-time consent prompt summarizing the points above.
              You can decline and continue to use the rest of the app normally. You can also revoke or re-grant your
              consent at any time at <strong>Profile → AI Preferences</strong>. Revoking consent disables all AI features
              for your account on this device until you re-enable them.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">4. Data Retention</h2>
            <p>
              We retain your account information for as long as your account is active. If you delete your
              account from the app (Profile → Delete my account), we soft-delete your record immediately:
              your personally identifiable information (email, name, avatar, bio, location, OAuth identifiers)
              is scrubbed, and your account is marked deleted. Aggregate, de-identified match statistics may
              be retained for leaderboard integrity. After 30 days the soft-deleted record is purged from
              backups in the ordinary backup-retention cycle.
            </p>
            <p>
              If you only contact us by email to request deletion, we will action it within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">5. Your Rights & Choices</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Access & download:</strong> Email {SUPPORT_EMAIL} and we will provide a copy of the personal data we hold on you within 30 days.</li>
              <li><strong>Correction:</strong> Edit your profile in-app at any time, or email us.</li>
              <li><strong>Deletion:</strong> Use Profile → Delete my account, or email {SUPPORT_EMAIL}.</li>
              <li><strong>Opt-out of marketing emails:</strong> Click "unsubscribe" in any marketing email. Transactional emails (password resets, account-security notices) cannot be opted out of while your account is active.</li>
              <li><strong>Push notifications:</strong> Toggle off in Settings → Notifications, or revoke at the OS level.</li>
              <li><strong>EU / UK (GDPR):</strong> You have rights of access, rectification, erasure, restriction, portability, and objection. To exercise them or to lodge a complaint with a supervisory authority, contact {SUPPORT_EMAIL}.</li>
              <li><strong>California (CCPA/CPRA):</strong> California residents have the right to know, to delete, to correct, to opt out of "sale" or "sharing" of personal information, and to non-discrimination. We do not sell your personal information.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">6. Security</h2>
            <p>
              We use industry-standard security including TLS in transit, encrypted database storage,
              hashed (bcrypt) passwords, HTTP-only session cookies, and least-privilege access controls.
              No system is perfectly secure — please use a unique, strong password and notify us immediately
              if you suspect unauthorized access to your account.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">7. Children's Privacy</h2>
            <p>
              {COMPANY} is not directed to children under 13 (or under 16 in the European Economic Area).
              We do not knowingly collect personal information from children. If you believe a child has
              provided us personal data, please email {SUPPORT_EMAIL} and we will delete the account.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">8. International Transfers</h2>
            <p>
              {COMPANY} is operated from the United States. If you use the Service from outside the U.S.,
              your information may be transferred to and processed in the U.S. or other countries where our
              service providers operate. Where required, we rely on standard contractual clauses or
              equivalent safeguards.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">9. Cookies & Local Storage</h2>
            <p>
              We use only the cookies and local-storage entries that are necessary to keep you signed in
              and to remember your preferences. We do not use third-party advertising or tracking cookies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">10. Changes to this Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will post the updated policy here and
              update the "Effective" date. Material changes will be notified to you in-app or by email.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mt-8 mb-3">11. Contact Us</h2>
            <p>
              For privacy questions or to exercise your rights, contact{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#9D4CDD] underline">{SUPPORT_EMAIL}</a>.
            </p>
          </section>
        </article>
      </main>
    </div>
  );
}
